import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import dayjs from 'dayjs'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  type SaleContract,
  useDeleteSaleContractMutation,
  useGetSaleContractCanHardDeleteQuery,
  useGetSaleContractsQuery,
} from '../../store/api/saleContractApi'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import saleContractApi from '../../store/api/saleContractApi'
import ContractFormDialog from './ContractFormDialog'
import type { SaleContractFormController } from './ContractFormDialog'
import PrintOffDialog from './PrintOffDialog'
import PrintOnDialog from './PrintOnDialog'
import { useAgentBridgeRegistry } from '../../agent/bridgeRegistry'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type FilterKind = 'search'
type FinalizedFilter = 'all' | 'yes' | 'no'

/**
 * Cột freeze khi scroll ngang — chỉ Contract Ref. ID + cột khác scroll bình thường.
 * Skill `/table-style` §7.6: keep visibleColumns natural order, set sticky-left
 * + opaque bg + z-index → cột trước nó scroll khuất phía sau khi cuộn phải.
 *
 * Width fallback theo viewport: mobile (<sm) hẹp hơn để chừa chỗ scroll, desktop
 * rộng đủ hiện header "Contract Ref" (bold 900) + sort/filter icons.
 */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['contractNo'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = {
  contractNo: 130,
}
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = {
  contractNo: 115,
}

/** TablePagination «All» maps to this pageSize (single request; real row count ≤ total). */
const ALL_ROWS_PAGE_SIZE = 1_000_000


/** Compact actions above grid (right-aligned; slightly larger than ultra-tight). */
const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number' | 'percent'
type ColumnDef = {
  key: keyof SaleContract
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

/** Column order + captions match DocConvert §8.5.2 (`frmContractSalesQryLocalS`). */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'number', align: 'right' },
  { key: 'contractNo', label: 'Contract Ref' },
  { key: 'buyerRef', label: "Buyer's Ref" },
  { key: 'contractDate', label: 'Contract Date', kind: 'date' },
  { key: 'clientCode', label: 'Client ID', filter: 'checkboxes' },
  { key: 'saleOffshoreOnshore', label: 'Client Type' },
  { key: 'clientShort', label: 'Client' },
  { key: 'consigneeName', label: 'Consignee' },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'bags', label: 'Bags', kind: 'number', align: 'right' },
  { key: 'packagingCode', label: 'Package Code' },
  { key: 'packaging', label: 'Package VN' },
  { key: 'packagingEng', label: 'Package ENG' },
  { key: 'qualityCode', label: 'Quality' },
  { key: 'certificationCode', label: 'Certification', filter: 'checkboxes' },
  { key: 'contractType', label: 'Contract Type', filter: 'checkboxes' },
  { key: 'price', label: 'Price', kind: 'number', align: 'right' },
  { key: 'priceUnit', label: 'Price Unit' },
  { key: 'ptbfDiff', label: 'PTBF diffs', kind: 'number', align: 'right' },
  { key: 'vatTxt', label: 'Applicable VAT' },
  { key: 'futuresMonth', label: 'Futures Month' },
  { key: 'fixationCall', label: 'Fixation Call' },
  { key: 'fxRate', label: 'FX', kind: 'number', align: 'right' },
  { key: 'deliveryTerm', label: 'Delivery Terms' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'shipmentDateFrom', label: 'Shipment From', kind: 'date' },
  { key: 'shipmentDateTo', label: 'Shipment To', kind: 'date' },
  { key: 'deliveryDateFrom', label: 'Delivery From', kind: 'date' },
  { key: 'deliveryDateTo', label: 'Delivery To', kind: 'date' },
  { key: 'pss', label: 'PSS Terms' },
  { key: 'paymentWeightTerm', label: 'Payment Weight Terms' },
  { key: 'franchise', label: 'Franchise', kind: 'percent', align: 'right' },
  { key: 'paymentTerms', label: 'Payment Terms' },
  { key: 'conditionsCode', label: 'Conditions' },
  { key: 'note', label: 'Note' },
  { key: 'sentContractText', label: 'Sent Contract' },
  { key: 'receivedContractText', label: 'Received Contract' },
  { key: 'sentPaperText', label: 'Sent Paper' },
  { key: 'receivedPaperText', label: 'Received Paper' },
  { key: 'futuresMonth2', label: 'Futures Month2' },
  { key: 'fxAuto', label: 'FX Auto', kind: 'number', align: 'right' },
  { key: 'qualityPremium', label: 'Quality Premium', kind: 'number', align: 'right' },
  { key: 'certPremium', label: 'Cert Premium', kind: 'number', align: 'right' },
  { key: 'packagePremium', label: 'Package Premium', kind: 'number', align: 'right' },
  { key: 'freight', label: 'Freight', kind: 'number', align: 'right' },
  { key: 'logisticsCost', label: 'Logistics Cost', kind: 'number', align: 'right' },
  { key: 'insuranceCost', label: 'Insurance Cost', kind: 'number', align: 'right' },
  { key: 'financeCost', label: 'Finance Cost', kind: 'number', align: 'right' },
  { key: 'bankCost', label: 'Bank Charge', kind: 'number', align: 'right' },
  { key: 'weightingCost', label: 'Weighing Cost', kind: 'number', align: 'right' },
  { key: 'g2FobDiffBulk', label: 'G2 FOB Fiff Bulk', kind: 'number', align: 'right' },
  { key: 'totalLot', label: 'Total Lot', kind: 'number', align: 'right' },
  { key: 'lotFixed', label: 'Lot Fixed', kind: 'number', align: 'right' },
  { key: 'lotToFix', label: 'Lot To Fix', kind: 'number', align: 'right' },
  { key: 'avgFuturesPrice', label: 'Avg Futures Price', kind: 'number', align: 'right' },
  { key: 'impliedDiffs', label: 'Implied Diffs', kind: 'number', align: 'right' },
  { key: 'kgFixed', label: 'Kg Fixed', kind: 'number', align: 'right' },
  { key: 'kgToFix', label: 'Kg To Fix', kind: 'number', align: 'right' },
  { key: 'avgPrice', label: 'Avg Price', kind: 'number', align: 'right' },
  { key: 'description', label: 'Description' },
  { key: 'finalizedText', label: 'Finalized', filter: 'checkboxes' },
  { key: 'finalizedDate', label: 'Finalized Date', kind: 'date' },
  { key: 'includedRoyal', label: 'Included Royal Fee ?' },
  { key: 'royalFee', label: 'Royal Fee', kind: 'number', align: 'right' },
  { key: 'matchingKg', label: 'Matched Quantity', kind: 'number', align: 'right' },
  { key: 'unmatchedKg', label: 'Unmatched Quantity', kind: 'number', align: 'right' },
  { key: 'brokerCode', label: 'Broker Code' },
  { key: 'brokerFirm', label: 'Broker Name' },
  { key: 'brokerRef', label: "Broker's Ref" },
  { key: 'commission', label: 'Commission', kind: 'number', align: 'right' },
  { key: 'traderName', label: 'Trader In Charge' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person' },
]

const NON_SUM_TOTAL_KEYS = new Set<keyof SaleContract>([
  'price',
  'ptbfDiff',
  'fxRate',
  'fxAuto',
  'avgFuturesPrice',
  'impliedDiffs',
  'avgPrice',
])

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SaleContract[], key: keyof SaleContract): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

interface ContractListViewProps {
  shipmentPeriodStart?: string
  shipmentPeriodEnd?: string
  finalizedFilter?: FinalizedFilter
}

export default function ContractListView({
  shipmentPeriodStart = '',
  shipmentPeriodEnd = '',
  finalizedFilter = 'all',
}: ContractListViewProps) {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  const { registerBridge, unregisterBridge, setActiveModule } = useAgentBridgeRegistry()
  // Row selection toggle (click row → focus, click lại → bỏ focus).
  // `setSelectedId` direct vẫn dùng cho DataBridge navigate + post-delete clear.
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [confirmDelete, setConfirmDelete] = useState<SaleContract | null>(null)

  // Email-allowlist gate for hard-delete (legacy `btnDelete` rule, parity with Purchase).
  // refetchOnMountOrArgChange ensures the check runs each time the page opens
  // (so re-login with a different account picks up the new email).
  const { data: deleteAccess } = useGetSaleContractCanHardDeleteQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const canHardDelete = canEdit && (deleteAccess?.canDelete ?? false)
  const [deleteSaleContract, { isLoading: deleting }] = useDeleteSaleContractMutation()
  const [page, setPage] = useState(0)
  /** Default «All»: request large pageSize (backend no longer caps list pageSize). */
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths('sale-contract.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SaleContract> | null>(null)

  // Global Search popover (filter bar)
  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  // Per-column filters (header ▽ icons). Lưu viewport position thay vì DOM
  // element để tránh popover về top-left khi TableVirtuoso remount header.
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SaleContract
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'new' | 'copy' | 'edit'>('new')
  const formControllerRef = useRef<SaleContractFormController | null>(null)

  const [printOffOpen, setPrintOffOpen] = useState(false)
  const [printOnOpen, setPrintOnOpen] = useState(false)

  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_contract',
    prefKey: 'columns',
    subKey: 'sale-list',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'sale-contract.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  // Freeze styling (sticky + opaque bg + stripe/hover/selected match) handled bởi
  // CSS `.app-sticky-col` trong `src/styles/app-shared.css` — chỉ cần add className.

  const prefetchClients = saleContractApi.usePrefetch('getLookupClients')
  const prefetchConsignees = saleContractApi.usePrefetch('getLookupConsignees')
  const prefetchPackages = saleContractApi.usePrefetch('getLookupPackages')
  const prefetchQualities = saleContractApi.usePrefetch('getLookupQualities')
  const prefetchBrokers = saleContractApi.usePrefetch('getLookupBrokers')
  const prefetchTraders = saleContractApi.usePrefetch('getLookupTraders')

  const prefetchFormLookups = () => {
    prefetchClients(undefined)
    prefetchConsignees(undefined)
    prefetchPackages(undefined)
    prefetchQualities(undefined)
    prefetchBrokers(undefined)
    prefetchTraders(undefined)
  }

  const {
    data: contractList,
    isLoading,
    isFetching,
    error,
  } = useGetSaleContractsQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
    },
    // Mỗi lần đổi tab Sales Contract → component remount → refetch lại data.
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => contractList?.items ?? [], [contractList])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => {
    return sortTableRows(filteredRows, sortState, TABLE_COLUMNS)
  }, [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(col.key))
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  /** Khi click ▽ trên header → compute popover kind + options + label. */
  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const idRangeText = useMemo(() => {
    if (rows.length === 0) return 'ID: —'
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const r of rows) {
      const id = Number(r.id)
      if (!Number.isFinite(id)) continue
      if (id < min) min = id
      if (id > max) max = id
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'ID: —'
    return `ID: ${min}–${max}`
  }, [rows])
  const minTableWidth = Math.max(960, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof SaleContract) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<SaleContract | null>(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  )

  const openDialog = (mode: 'new' | 'copy' | 'edit') => {
    if ((mode === 'copy' || mode === 'edit') && !selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    if (mode === 'edit' && selectedRow && (selectedRow.finalizedText ?? '').trim().toUpperCase() !== 'N') {
      window.alert('This contract is finalized! No edit')
      return
    }
    setDialogMode(mode)
    setDialogOpen(true)
  }

  const handleDeleteClick = () => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return
    }
    if ((selectedRow.finalizedText ?? '').trim().toUpperCase() !== 'N') {
      snack.warning('This contract is finalized! No delete')
      return
    }
    setConfirmDelete(selectedRow)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteSaleContract(confirmDelete.id).unwrap()
      snack.success(`Contract ${confirmDelete.contractNo} deleted`)
      if (selectedId === confirmDelete.id) setSelectedId(null)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete contract failed'))
    }
  }

  const cellValue = (row: SaleContract, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'percent' && typeof val === 'number') return `${(val * 100).toFixed(2)}%`
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'boolean') return val ? 'Yes' : 'No'
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  // Ref pattern: bridge methods đọc state mới nhất qua ref, useEffect chỉ đăng ký
  // 1 lần với deps stable. Tránh deps đổi mỗi render → infinite register loop.
  const bridgeStateRef = useRef({ rows, selectedRow, dialogOpen, openDialog })
  bridgeStateRef.current = { rows, selectedRow, dialogOpen, openDialog }

  useEffect(() => {
    setActiveModule('sale-contract')
    registerBridge('sale-contract', {
      openForm: async (mode) => {
        bridgeStateRef.current.openDialog(mode)
        return `Sales Contract form opened (${mode}).`
      },
      selectRow: async (selector) => {
        const { rows } = bridgeStateRef.current
        const normalized = String(selector).trim().toLowerCase()
        const found = rows.find(
          (row) =>
            row.id === Number(selector) ||
            row.contractNo.toLowerCase() === normalized ||
            String(row.id) === normalized,
        )
        if (!found) throw new Error(`Sales Contract not found for selector: ${selector}`)
        setSelectedId(found.id)
        return `Selected Sales Contract #${found.id} (${found.contractNo}).`
      },
      setField: async (field, value) => {
        if (!bridgeStateRef.current.dialogOpen || !formControllerRef.current) throw new Error('Sale form is not open.')
        return formControllerRef.current.setField(field, value)
      },
      saveForm: async () => {
        if (!bridgeStateRef.current.dialogOpen || !formControllerRef.current) throw new Error('Sale form is not open.')
        return formControllerRef.current.save()
      },
      cancelForm: async () => {
        if (!bridgeStateRef.current.dialogOpen || !formControllerRef.current) throw new Error('Sale form is not open.')
        return formControllerRef.current.cancel()
      },
      getState: async () => {
        const { dialogOpen, selectedRow } = bridgeStateRef.current
        if (!dialogOpen || !formControllerRef.current) {
          return selectedRow
            ? `Sale list ready. selectedId=${selectedRow.id}, contractNo=${selectedRow.contractNo}.`
            : 'Sale list ready. No row selected.'
        }
        return formControllerRef.current.getState()
      },
    })

    return () => {
      unregisterBridge('sale-contract')
      setActiveModule(null)
    }
  }, [registerBridge, unregisterBridge, setActiveModule])

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Action + filter bar — template dataList */}
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={() => openDialog('new')}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={() => openDialog('copy')}
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              title="Copy"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={() => openDialog('edit')}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canEdit}
              onClick={() => setPrintOffOpen(true)}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print-Off"
            >
              {/* Discriminator "Off" luôn show (kể cả xs) để phân biệt với Print-On.
                  md+ thêm prefix "Print-" cho full text. */}
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>
              Off
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canEdit}
              onClick={() => setPrintOnOpen(true)}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print-On"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>
              On
            </Button>
            {canHardDelete && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!selectedRow || !canEdit}
                onClick={handleDeleteClick}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
            )}
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Sales Contract.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        {/* Filter bar + pagination */}
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Loaded: {baseRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

      {/* Global Search popover */}
      <FilterPopover
        open={filterPopover.state !== null}
        anchorEl={filterPopover.state?.anchor ?? null}
        onClose={closeFilterPopover}
        maxWidth={320}
      >
        {filterPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); setPage(0) }}
            placeholder="Contract no, client..."
            label="Search contracts"
            onClear={closeFilterPopover}
          />
        )}
      </FilterPopover>

      {/* Per-column filter popover (▽ icon trên header) */}
      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={closeColPopover}
        label={popoverColumn?.label ?? ''}
        kind={(popoverKind === 'none' ? 'text' : popoverKind)}
        value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(String(colPopover.key), v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      {/* Virtualized table (react-virtuoso) — no 100-row cap */}
      <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
        {error ? (
          <BrandState
            tone="error"
            title="Could not load Sales Contract"
            description="Please refresh the page or contact the system administrator if the issue continues."
          />
        ) : isLoading && baseRows.length === 0 ? (
          <BrandState tone="loading" title="Loading…" description="Please wait." />
        ) : rows.length === 0 ? (
          <BrandState
            title="No Sales Contract found"
            description="Try adjusting the saved shipment period, finalized filter, or the table filters above."
          />
        ) : (
          <TableVirtuoso
          key={gridKey}
          style={{ height: '100%' }}
          data={rows}
          fixedHeaderContent={() => (
            <TableRow>
              {visibleColumns.map((col) => {
                const key = String(col.key)
                const isFrozen = FROZEN_KEYS.has(key)
                const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                // Inline style cho sticky positioning — pattern MatchInstructTab/PContractTab:
                // specificity 1,0,0,0 luôn thắng MUI `.MuiTableCell-stickyHeader` z-index 2,
                // tránh stacking-context isolation trong TableVirtuoso khiến body row đè header.
                const headerStyle: CSSProperties = {}
                if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                if (isFrozen) {
                  headerStyle.position = 'sticky'
                  headerStyle.top = 0
                  headerStyle.left = 0
                  headerStyle.zIndex = 100
                }
                return (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    className="app-table-header-cell"
                    onClick={() => toggleSort(col.key)}
                    style={Object.keys(headerStyle).length ? headerStyle : undefined}
                    sx={{
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      userSelect: 'none',
                      ...(isFrozen ? {} : { position: 'relative' }),
                    }}
                  >
                    {col.label}
                    {getTableSortSuffix(sortState, col.key)}
                    <ColumnFilterIcon
                      active={columnFilters.isActive(key)}
                      onClick={(position) => setColPopover({ key: col.key, position })}
                      ariaLabel={`Filter ${col.label}`}
                    />
                    <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                  </TableCell>
                )
              })}
            </TableRow>
          )}
          fixedFooterContent={() => (
            <TableRow>
              {visibleColumns.map((col, index) => {
                const key = String(col.key)
                const isFrozen = FROZEN_KEYS.has(key)
                const total = totalRowValues.get(col.key)
                const val = total == null ? '' : formatAccessNumber(total)
                const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                return (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    className={isFrozen ? 'app-sticky-col' : undefined}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      py: 0.5,
                      bgcolor: 'background.paper',
                      borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {index === 0 ? 'Total' : val}
                  </TableCell>
                )
              })}
            </TableRow>
          )}
          itemContent={(_: number, row: SaleContract) =>
            visibleColumns.map((col) => {
              const key = String(col.key)
              const isFrozen = FROZEN_KEYS.has(key)
              const val = cellValue(row, col)
              const w = getWidth(key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
              return (
                <TableCell
                  key={col.key}
                  align={col.align}
                  className={isFrozen ? 'app-sticky-col' : undefined}
                  style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                  sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {val || '-'}
                  </Box>
                </TableCell>
              )
            })
          }
          components={{
            Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
            // react-virtuoso expects plain HTML section components; MUI types are stricter, so we pass `component`
            // and loosen typing to avoid React type duplication issues in this repo.
            TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
            TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
            TableRow: (props: any) => {
              const idx = Number((props as any)['data-item-index'])
              const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
              return (
                <TableRow
                  {...props}
                  hover
                  selected={rowId != null && rowId === selectedId}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    if (rowId == null) return
                    toggleSelect(rowId)
                    prefetchFormLookups()
                  }}
                />
              )
            },
          }}
          />
        )}
      </Box>
      </Box>

      {/* Dialogs */}
      <ContractFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        mode={dialogMode}
        contractId={selectedRow?.id}
        contractNo={selectedRow?.contractNo}
        controllerRef={formControllerRef}
      />
      <PrintOffDialog
        open={printOffOpen}
        onClose={() => setPrintOffOpen(false)}
        contractId={selectedRow?.id ?? null}
        contractNo={selectedRow?.contractNo ?? ''}
        clientShort={selectedRow?.clientShort ?? ''}
        canEdit={canEdit}
      />
      <PrintOnDialog
        open={printOnOpen}
        onClose={() => setPrintOnOpen(false)}
        contractId={selectedRow?.id ?? null}
        contractNo={selectedRow?.contractNo ?? ''}
        clientShort={selectedRow?.clientShort ?? ''}
        canEdit={canEdit}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Contract"
        message={confirmDelete ? `Do you want to delete Contract S = '${confirmDelete.contractNo}' ?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
